import { ToneObject } from './openai/tone-map';
import { SummaryType } from './summary-types';

export interface IAccount {
  _id: string;
  name?: string;
  logo?: string;
  ownerId?: string;
  members?: IAccountMember[];
  publishTargets?: IUserPublishTargets;
  directions?: string;
  invites?: IAccountInvite[];
  aggregatorKeywords?: string[];
  aggregatorCategories?: string[];
  biasThresholdMin?: number;
  biasThresholdMax?: number;
  reliabilityThreshold?: number;
  titleExamples?: ITitleExample[];
  defaultTone?: string;
  customTones?: Array<ToneObject>;
  imageTemplateSettings?: IImageTemplateSettings;
  settings?: any;
  active?: boolean;
  createdAt?: string;
  updatedAt?: string;
  isBusiness?: boolean;
  defaultInstagramPostType?: 'FEED' | 'REEL' | 'STORY';
  defaultAiProvider?: DefaultAiProvider;
  terminologyCount?: number;
  analyzedTone?: ToneObject;
}

export type WordPressHostType = 'self-hosted' | 'wordpress-com';

export type WordPressPublishAuthType =
  | 'application-password'
  | 'wpcom-oauth';

export type WordPressMetricsAuthType =
  | 'none'
  | 'core-rest'
  | 'wpcom-oauth'
  | 'custom-plugin';

export interface IWordPressOAuthConnection {
  accessToken?: string;
  refreshToken?: string;
  expiresAt?: number;
  scope?: string;
  tokenType?: string;
  siteId?: number;
  siteSlug?: string;
  siteUrl?: string;
}


export interface IUserPublishTargets {
  [key: string]: IUserPublishTarget | undefined;
  facebook?: IUserPublishTarget;
  instagram?: IUserPublishTarget;
  twitter?: IUserPublishTarget;
  wordpress?: IUserPublishTarget;
  bluesky?: IUserPublishTarget;
  mastodon?: IUserPublishTarget;
  threads?: IUserPublishTarget;
  // Add more keys for other publish targets if needed
}

export interface IAccountMember {
  userId: string;
  role?: AccountMemberRole;
  invitedBy?: string;
  invitedAt?: Date | string;
  acceptedAt?: Date | string;
  firstName?: string;
  lastName?: string;
  email?: string;
  logo?: string;
}

export interface IAccountInvite {
  tokenHash: string;
  email?: string;
  role?: AccountMemberRole;
  invitedBy?: string;
  createdAt?: Date | string;
  expiresAt?: Date | string;
  usedAt?: Date | string;
}

export interface IUserPublishTarget {
  url?: string;
  apiUser?: string;
  apiKey?: string;
  accessToken?: string;
  tokenType?: string;
  tokenExpiresIn?: number;
  tokenExpiresAt?: Date | string;
  tokenRefreshedAt?: Date | string;
  accessSecret?: string;
  summaryType?: SummaryType;
  useSocialCard?: boolean;
  wordpressPermalinkStructure?: 'plain' | 'day' | 'month' | 'numeric' | 'postname' | 'custom';
  wordpressCustomPermalink?: string;
  wordpressTargetPostType?: WordPressTargetPostType;
  wordpressIgnoreTopLevelCategory?: boolean;     //wordpress only
  wordpressUseGutenbergBlocks?: boolean;           //wordpress only
  wordpressHostType?: WordPressHostType;
  wordpressPublishAuthType?: WordPressPublishAuthType;
  wordpressMetricsAuthType?: WordPressMetricsAuthType;
  wordpressOAuthConnection?: IWordPressOAuthConnection;
  wordpressHasApplicationPassword?: boolean;
  wordpressHasOAuth?: boolean;
  pageId?: string;                      //facebook only I think
  facebookAppScopedUserId?: string;     //facebook only
  plugins?: string[];                   //wordpress only
  followers?: number;
  followerQuality?: IFollowerQualitySnapshot;
  valid?: boolean;
  errorCode?: string;
  active?: boolean;
  instagramPostType?: 'FEED' | 'REEL' | 'STORY';
  instagramCapabilities?: {
    canPublish: boolean;
    lastCheckedAt?: Date;
    rawErrorCode?: number;
    blockedReason?: string;
  };
}

export type FollowerQualityBand = 'high' | 'medium' | 'low' | 'unknown';

export interface IFollowerQualitySnapshot {
  source: 'bluesky' | 'mastodon' | 'derived';
  calculatedAt: string;
  netFollowerChange: number;
  followerGrowthRatePct: number;
  engagementPerFollower30d: number;
  followerToFollowingRatio: number;
  followingCount: number;
  postsCount: number;
  score: number;
  band: FollowerQualityBand;
}


export interface ITitleExample {
  content: string;
  goodTitleExamples?: string[];
  badTitleExamples?: string[];
}


export interface IImageTemplateSettings {
  template?: ImageTemplate;
  backgroundColor?: string;
  highlightColor?: string;
  fontColor?: string;
  textCase?: ImageTemplateTextCase;
  imagePlacement?: ImagePlacement;
  fontFamily?: string;
}


export enum AccountMemberRole {
  OWNER = 'owner',
  ADMIN = 'admin',
  EDITOR = 'editor',
  VIEWER = 'viewer',
}

/**
 * Keep UI-side allowed lists/types in sync with social-dash-core.
 * These mirror social-dash-core/src/models/account.interface.ts
 */
export const DEFAULT_AI_PROVIDERS = ['gpt-4o', 'claude-haiku-4-5', 'claude-sonnet-4-5'] as const;
export type DefaultAiProvider = typeof DEFAULT_AI_PROVIDERS[number];

export const IMAGE_TEMPLATES = [
  'none',
  'title-only',
  'title-blurb',
  'stacked-left',
  'stacked-center',
  'framed-headline-photo-top',
  'editorial-sidebar',
  'spotlight-panel',
  'news-banner-top',
  'poster-frame',
  'news-breaking-slate',
  'news-focus-split',
  'quote-classic-serif',
  'quote-profile-highlight',
] as const;
export type ImageTemplate = typeof IMAGE_TEMPLATES[number];

export type ImageTemplateTextCase = 'uppercase' | 'title' | 'sentence' | 'none';
export type ImagePlacement = 'attention' | 'center' | 'top' | 'bottom' | 'fit-blur' | 'fit-color' | 'fit-theme';

export const DEFAULT_IMAGE_PLACEMENT: ImagePlacement = 'attention';

export const TEMPLATE_DEFAULT_IMAGE_PLACEMENTS: Readonly<Partial<Record<ImageTemplate, ImagePlacement>>> = {
  'editorial-sidebar': 'center',
  'news-focus-split': 'center',
  'quote-profile-highlight': 'center',
};

/**
 * Resolve the default image placement for a social card template.
 * Falls back to Smart Crop (attention) when no template-specific default is set.
 *
 * @param template - Selected image template
 * @returns The default image placement for that template
 */
export function getDefaultImagePlacementForTemplate(template?: ImageTemplate | null): ImagePlacement {
  if (!template) {
    return DEFAULT_IMAGE_PLACEMENT;
  }
  return TEMPLATE_DEFAULT_IMAGE_PLACEMENTS[template] ?? DEFAULT_IMAGE_PLACEMENT;
}

export type publishTargetKeys = keyof IUserPublishTargets;
export type WordPressTargetPostType = 'link' | 'photo' | 'social-card' | 'text';